import { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";
import SizeGroupModal from "../../components/masters/SizeGroupModal";

export default function SizeGroupsPage() {
  const { accessToken } = useAuth();

  const [sizeGroups, setSizeGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSizeGroup, setEditingSizeGroup] = useState(null);

  const loadSizeGroups = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/size-groups", { accessToken });

      setSizeGroups(Array.isArray(response) ? response : response?.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load size groups.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadSizeGroups();
  }, [loadSizeGroups]);

  const filteredSizeGroups = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return sizeGroups;

    return sizeGroups.filter((sizeGroup) =>
      [sizeGroup.name, sizeGroup.code].some((value) =>
        value?.toLowerCase().includes(query),
      ),
    );
  }, [search, sizeGroups]);
  const sizeGroupsPagination = useClientPagination(filteredSizeGroups);

  const handleSave = async (form) => {
    setError("");

    try {
      if (editingSizeGroup) {
        await apiRequest(`/size-groups/${editingSizeGroup.id}`, {
          method: "PATCH",
          body: form,
          accessToken,
        });
      } else {
        await apiRequest("/size-groups", {
          method: "POST",
          body: form,
          accessToken,
        });
      }

      setIsModalOpen(false);
      setEditingSizeGroup(null);
      await loadSizeGroups();
    } catch (requestError) {
      setError(requestError.message || "Unable to save size group.");
    }
  };

  const handleDelete = async (sizeGroup) => {
    const confirmed = window.confirm(
      `Delete "${sizeGroup.name}"? This only works when it is not assigned to any sizes or subcategories.`,
    );

    if (!confirmed) return;

    setError("");

    try {
      await apiRequest(`/size-groups/${sizeGroup.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadSizeGroups();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete size group.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-primary-800">
            Masters
          </p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">Size Groups</h1>
          <p className="mt-2 text-sm text-gray-500">
            Define reusable size sets and link them to subcategories.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingSizeGroup(null);
            setIsModalOpen(true);
          }}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 text-sm font-semibold text-white hover:bg-primary-900"
        >
          <FiPlus size={18} />
          Add Size Group
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="relative max-w-md">
          <FiSearch
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by group name or code..."
            className="h-11 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-6 py-4 font-semibold">Size Group</th>
                <th className="px-6 py-4 font-semibold">Code</th>
                <th className="px-6 py-4 font-semibold">Used By</th>
                <th className="px-6 py-4 font-semibold">Order</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 text-right font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-sm text-gray-500"
                  >
                    Loading size groups...
                  </td>
                </tr>
              ) : filteredSizeGroups.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-sm text-gray-500"
                  >
                    No size groups found.
                  </td>
                </tr>
              ) : (
                sizeGroupsPagination.pageItems.map((sizeGroup) => {
                  const sizeCount = sizeGroup._count?.sizes ?? 0;
                  const subCategoryCount = sizeGroup._count?.subCategories ?? 0;

                  return (
                    <tr key={sizeGroup.id} className="hover:bg-gray-50/70">
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        {sizeGroup.name}
                      </td>
                      <td className="px-6 py-4">
                        <span className="rounded-lg bg-gray-100 px-2.5 py-1 font-mono text-xs font-medium text-gray-700">
                          {sizeGroup.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {sizeCount} sizes · {subCategoryCount} subcategories
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {sizeGroup.sortOrder}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            sizeGroup.isActive
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {sizeGroup.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingSizeGroup(sizeGroup);
                              setIsModalOpen(true);
                            }}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-primary-50 hover:text-primary-800"
                            title="Edit size group"
                          >
                            <FiEdit2 size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(sizeGroup)}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600"
                            title="Delete size group"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={sizeGroupsPagination} label="size groups" />
      </div>

      <SizeGroupModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingSizeGroup(null);
        }}
        onSave={handleSave}
        initialData={editingSizeGroup}
      />
    </div>
  );
}
